import * as React from "react";
import { cloneElement, useCallback, useEffect, useState } from "react";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import axios from "axios";
import { GridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import { Grid, Typography, useTheme } from "@mui/material";
import {
  GridCellProps,
  GridDataStateChangeEvent,
  GridItemChangeEvent,
  GridRowProps,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { process } from "@progress/kendo-data-query";
import { faPlus, faRefresh, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { TTProtection, TTProtectionFilters } from "../../TTProtection";
import { SprKey, sprKeysAreEqual } from "../../SprKey";
import { ttProtectionFilterUrls } from "../../../../URL/ApplicationUrls";
import StyledKendoGrid from "../../../../Grid/StyledKendoGrid";
import { InitialState } from "../../../../Grid/InitialDataState";
import { KENDO_BUTTON_CLASSES } from "../../../../Layout/Buttons/KendoButtons";
import { UppercaseButton } from "../../../../Layout/Buttons/UppercaseButton";
import { IconWithRightMargin } from "../../../../Layout/Icons/IconWithRightMargin";
import { apiErrorNotificationToast } from "../../../../Errors/apiErrorNotificationToast";
import LoadingPanel from "../../../../Layout/LoadingPanel";
import { EDIT_FIELD } from "../../../../Grid/GridConstants";
import { AffectedSprsCommandCell } from "./AffectedSprsCommandCell";
import { asDeletableSprs, asExcludableSprs, EXCLUDED_FIELD, excludedSprsStyle } from "./ManualSprs";
import { nameOf } from "../../../../Utils/nameOf";

type SprKeyWithEditField = SprKey & { [EDIT_FIELD]: boolean };

const sprKeyDefaultAddValues: SprKeyWithEditField = {
  SupplierGsdb: "",
  PlantGsdb: "",
  [EDIT_FIELD]: true,
};

export const AffectedSprsTable = () => {
  const {
    control,
    getValues,
    setValue,
    formState: { disabled: isFormDisabled },
  } = useFormContext<TTProtection>();

  const [sprsMatchingFilters, setSprsMatchingFilters] = useState<SprKey[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchAffectedSprs = useCallback((filters: TTProtectionFilters) => {
    setIsLoading(true);
    axios
      .post<SprKey[]>(ttProtectionFilterUrls.affectedSprsByTtProtectionFilters, filters)
      .then((it) => {
        setSprsMatchingFilters(it.data);
        if (it.data.length > 0)
          toast.success(`${it.data.length} SPRs matching your current filters found`);
        else toast.warning("No SPRs matching your current filters found");
      })
      .catch((error) => apiErrorNotificationToast(error))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchAffectedSprs(getValues("TtProtectionFilters"));
  }, [fetchAffectedSprs, getValues]);

  const [dataState, setDataState] = useState(InitialState);
  const onDataStateChanged = (event: GridDataStateChangeEvent) => {
    setDataState(event.dataState);
  };

  const [sprKeyToAdd, setSprKeyToAdd] = useState<SprKeyWithEditField | undefined>(undefined);
  const addNew = () => {
    setSprKeyToAdd(sprKeyDefaultAddValues);
  };

  function addSprKeyToFormContextValue(
    path: FieldPath<TTProtection>,
    sprKey: SprKey,
    currentValue: SprKey[]
  ) {
    const newValue = {
      SupplierGsdb: sprKey.SupplierGsdb,
      PlantGsdb: sprKey.PlantGsdb,
    };
    setValue(path, [newValue, ...currentValue], { shouldValidate: true, shouldDirty: true });
  }

  function removeSprKeyFromFormContextValue(
    path: FieldPath<TTProtection>,
    sprKey: SprKey,
    currentValue: SprKey[]
  ) {
    const newValue = [...currentValue];
    const index = newValue.findIndex((it) => sprKeysAreEqual(it, sprKey));

    if (index !== -1) {
      newValue.splice(index, 1);
      setValue(path, newValue, { shouldValidate: true, shouldDirty: true });
    }
  }

  const manuallyAddedSprs = useWatch({
    control,
    name: "ManuallyAddedSprs",
  });
  const addManuallyAddedSprKey = () => {
    if (!sprKeyToAdd) return;
    addSprKeyToFormContextValue("ManuallyAddedSprs", sprKeyToAdd, manuallyAddedSprs);
    setSprKeyToAdd(undefined);
  };

  const excludedSprs = useWatch({
    control,
    name: "ExcludedSprs",
  });
  const excludeMatchingSprKey = (sprKeyToExclude: SprKey) => {
    if (!sprKeyToExclude) return;
    addSprKeyToFormContextValue("ExcludedSprs", sprKeyToExclude, excludedSprs);
  };

  const affectedSprsCommandCell = (gridCellProps: GridCellProps, disabled: boolean) => (
    <AffectedSprsCommandCell
      gridCellProps={gridCellProps}
      addManuallyAddedSprKey={addManuallyAddedSprKey}
      cancelAdd={() => setSprKeyToAdd(undefined)}
      removeManuallyAddedSprKey={(sprKey: SprKey) =>
        removeSprKeyFromFormContextValue("ManuallyAddedSprs", sprKey, manuallyAddedSprs)
      }
      excludeSprKey={excludeMatchingSprKey}
      revokeExclusion={(sprKey: SprKey) =>
        removeSprKeyFromFormContextValue("ExcludedSprs", sprKey, excludedSprs)
      }
      disableCommandIcons={disabled}
    />
  );

  const onItemChange = (event: GridItemChangeEvent) => {
    setSprKeyToAdd((prev) =>
      prev
        ? {
            ...prev,
            [event.field || ""]: (event.value as string).toUpperCase(),
          }
        : undefined
    );
  };

  const theme = useTheme();
  const highlightExcludedItems = (
    trElement: React.ReactElement<HTMLTableRowElement>,
    rowProps: GridRowProps
  ) => {
    const style = rowProps.dataItem[EXCLUDED_FIELD] ? excludedSprsStyle(theme) : {};
    return cloneElement(
      trElement,
      { style: style as CSSStyleDeclaration },
      trElement.props.children
    );
  };

  const ExcelExportRef = React.useRef<ExcelExport | null>(null);
  const exportAffectedSprs = () => {
    if (ExcelExportRef.current !== null) {
      ExcelExportRef.current.save(sprs);
    }
  };

  const sprs = [
    ...(sprKeyToAdd ? [sprKeyToAdd] : []),
    ...asDeletableSprs(manuallyAddedSprs),
    ...asExcludableSprs(sprsMatchingFilters, excludedSprs),
  ];

  return (
    <>
      <Typography
        sx={{
          fontWeight: "bold",
          mt: "1.5rem",
        }}
      >
        Affected SPRs
      </Typography>
      <Grid
        container
        sx={{
          mt: "1rem",
          pr: "2rem",
        }}
      >
        <LoadingPanel isLoading={isLoading} />
        <ExcelExport ref={ExcelExportRef} fileName="ExportSpr" data={sprs}>
          <ExcelExportColumn
            key="supplier"
            title="Supplier"
            field={nameOf<SprKey>("SupplierGsdb")}
          />
          <ExcelExportColumn key="plant" title="Plant" field={nameOf<SprKey>("PlantGsdb")} />
        </ExcelExport>
        <StyledKendoGrid
          data={process(sprs, dataState)}
          {...dataState}
          onDataStateChange={(event) => onDataStateChanged(event)}
          muiTheme={theme}
          height="65vh"
          filterable
          sortable
          pageable={{ buttonCount: 5 }}
          editField={EDIT_FIELD}
          onItemChange={onItemChange}
          rowRender={highlightExcludedItems}
        >
          <GridToolbar>
            <UppercaseButton
              type="button"
              title="ADD SPR"
              className={KENDO_BUTTON_CLASSES}
              onClick={addNew}
              disabled={!!sprKeyToAdd || isFormDisabled}
            >
              <IconWithRightMargin icon={faPlus} />
              ADD SPR
            </UppercaseButton>
            <UppercaseButton
              type="button"
              title="Reload SPR"
              className={KENDO_BUTTON_CLASSES}
              onClick={() => fetchAffectedSprs(getValues("TtProtectionFilters"))}
            >
              <IconWithRightMargin icon={faRefresh} />
              Reload SPR
            </UppercaseButton>
            <UppercaseButton
              type="button"
              title="Export SPR"
              className={KENDO_BUTTON_CLASSES}
              onClick={exportAffectedSprs}
            >
              <IconWithRightMargin icon={faUpRightFromSquare} />
              Export SPR
            </UppercaseButton>
          </GridToolbar>
          <GridColumn
            key="supplier"
            title="Supplier"
            field={nameOf<SprKey>("SupplierGsdb")}
            filter="text"
          />
          <GridColumn key="plant" title="Plant" field={nameOf<SprKey>("PlantGsdb")} filter="text" />
          <GridColumn
            key="commandColumn"
            title="Actions"
            filterable={false}
            cell={(gridCellProps) => affectedSprsCommandCell(gridCellProps, isFormDisabled)}
            width="110px"
          />
        </StyledKendoGrid>
      </Grid>
    </>
  );
};
